import React, { useState, useEffect, useCallback } from 'react';
import prospectionApi from '../services/prospection';
import ProspectionContactsCell from './ProspectionContactsCell';
import './ProspectionB2B.css';

/* ── Référentiels ────────────────────────────────────────── */
const DEPARTEMENTS = [
  '01','02','03','04','05','06','07','08','09','10','11','12','13','14','15','16','17','18','19',
  '2A','2B','21','22','23','24','25','26','27','28','29','30','31','32','33','34','35','36','37',
  '38','39','40','41','42','43','44','45','46','47','48','49','50','51','52','53','54','55','56',
  '57','58','59','60','61','62','63','64','65','66','67','68','69','70','71','72','73','74','75',
  '76','77','78','79','80','81','82','83','84','85','86','87','88','89','90','91','92','93','94','95',
  '971','972','973','974','976'
];

const NATURES_JURIDIQUES = [
  { value: '9220', label: 'Association déclarée' },
  { value: '1000', label: 'Entrepreneur individuel' },
  { value: '5505', label: 'SA à conseil d\'administration' },
  { value: '5410', label: 'SARL' },
  { value: '5499', label: 'SARL unipersonnelle (EURL)' },
  { value: '5710', label: 'SAS' },
  { value: '5720', label: 'SASU' },
  { value: '5610', label: 'SCA' },
  { value: '6210', label: 'SCOP' },
  { value: '5306', label: 'SNC' },
];

const TRANCHES_EFFECTIF = [
  { value: '00', label: '0 salarié' },
  { value: '01', label: '1-2 salariés' },
  { value: '02', label: '3-5 salariés' },
  { value: '03', label: '6-9 salariés' },
  { value: '11', label: '10-19 salariés' },
  { value: '12', label: '20-49 salariés' },
  { value: '21', label: '50-99 salariés' },
  { value: '22', label: '100-199 salariés' },
  { value: '31', label: '200-249 salariés' },
  { value: '32', label: '250-499 salariés' },
  { value: '41', label: '500-999 salariés' },
  { value: '42', label: '1000-1999 salariés' },
  { value: '51', label: '2000-4999 salariés' },
  { value: '52', label: '5000-9999 salariés' },
  { value: '53', label: '10000+ salariés' },
];

const STATUT_LABELS = {
  nouveau: 'Nouveau',
  contacte: 'Contacté',
  qualifie: 'Qualifié',
  rejete: 'Rejeté',
};

const SCORE_DETAIL_LABELS = {
  creation_recente: 'Création récente',
  dirigeant_identifie: 'Dirigeant identifié',
  dirigeant_recent: 'Dirigeant récent',
  taille_cible: 'Taille cible (TPE/PME)',
  multi_etablissements: 'Multi-établissements',
};

/* ── Composant principal ─────────────────────────────────── */
function ProspectionB2B({ user, editMode, setHeaderAction }) {
  const [tab, setTab] = useState('recherche');

  // ── Recherche live ──
  const [searchParams, setSearchParams] = useState({
    q: '', code_naf: '', departement: '', code_postal: '',
    nature_juridique: '', etat_administratif: 'A', tranche_effectif_salarie: '',
    est_ess: false, est_rge: false, est_bio: false,
    est_association: false, est_entrepreneur_individuel: false,
  });
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // ── Recherches sauvegardées ──
  const [recherches, setRecherches] = useState([]);
  const [loadingRecherches, setLoadingRecherches] = useState(false);
  const [selectedRecherche, setSelectedRecherche] = useState(null);
  const [rechercheProspects, setRechercheProspects] = useState([]);
  const [selectedSaved, setSelectedSaved] = useState(new Set());

  // ── Modales ──
  const [showDetail, setShowDetail] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Enrichissement ──
  const [enrichingIds, setEnrichingIds] = useState(new Set());
  const [quotaReached, setQuotaReached] = useState(false);
  const [showAudit, setShowAudit] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  // ── Header actions ──
  useEffect(() => {
    if (!setHeaderAction) return;
    if (tab === 'recherche' && results.length > 0) {
      setHeaderAction({
        primary: { label: '+ Sauvegarder', onClick: () => setShowSaveModal(true) },
      });
    } else if (tab === 'sauvegardees' && selectedRecherche) {
      const exportIcon = (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      );
      setHeaderAction({
        secondary: [
          {
            label: <>{exportIcon}{' '}Export Excel</>,
            onClick: () => handleExportXLSX(selectedRecherche.id),
          },
          {
            label: <>{exportIcon}{' '}Export CSV</>,
            onClick: () => handleExportCSV(selectedRecherche.id),
          },
        ],
      });
    } else {
      setHeaderAction(null);
    }
  }, [tab, results.length, selectedRecherche, setHeaderAction]); // eslint-disable-line

  // ── Recherche ──
  const handleSearch = useCallback(async (page = 1) => {
    setSearching(true);
    setHasSearched(true);
    try {
      const params = { page, per_page: 25 };
      if (searchParams.q) params.q = searchParams.q;
      if (searchParams.code_naf) params.code_naf = searchParams.code_naf;
      if (searchParams.departement) params.departement = searchParams.departement;
      if (searchParams.code_postal) params.code_postal = searchParams.code_postal;
      if (searchParams.nature_juridique) params.nature_juridique = searchParams.nature_juridique;
      if (searchParams.etat_administratif) params.etat_administratif = searchParams.etat_administratif;
      if (searchParams.tranche_effectif_salarie) params.tranche_effectif_salarie = searchParams.tranche_effectif_salarie;
      if (searchParams.est_ess) params.est_ess = 'true';
      if (searchParams.est_rge) params.est_rge = 'true';
      if (searchParams.est_bio) params.est_bio = 'true';
      if (searchParams.est_association) params.est_association = 'true';
      if (searchParams.est_entrepreneur_individuel) params.est_entrepreneur_individuel = 'true';

      const res = await prospectionApi.search(params);
      setResults(res.data.results || []);
      setTotalResults(res.data.total_results || 0);
      setTotalPages(res.data.total_pages || 0);
      setCurrentPage(res.data.page || 1);
      setSelected(new Set());
    } catch (e) {
      console.error('Erreur recherche:', e);
      alert(e.response?.data?.error || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  }, [searchParams]);

  const handleReset = () => {
    setSearchParams({
      q: '', code_naf: '', departement: '', code_postal: '',
      nature_juridique: '', etat_administratif: 'A', tranche_effectif_salarie: '',
      est_ess: false, est_rge: false, est_bio: false,
      est_association: false, est_entrepreneur_individuel: false,
    });
    setResults([]);
    setTotalResults(0);
    setHasSearched(false);
    setSelected(new Set());
  };

  // ── Sauvegarde ──
  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const prospects = selected.size > 0
        ? results.filter((_, i) => selected.has(i))
        : results;
      await prospectionApi.saveRecherche({
        nom: saveName.trim(),
        criteres: searchParams,
        prospects,
      });
      setShowSaveModal(false);
      setSaveName('');
      alert(`${prospects.length} prospect(s) sauvegardé(s)`);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // ── Recherches sauvegardées ──
  const loadRecherches = useCallback(async () => {
    setLoadingRecherches(true);
    try {
      const res = await prospectionApi.getRecherches();
      setRecherches(res.data.recherches || []);
    } catch (e) {
      console.error('Erreur chargement recherches:', e);
    } finally {
      setLoadingRecherches(false);
    }
  }, []);

  const loadRecherche = async (id) => {
    try {
      const res = await prospectionApi.getRecherche(id);
      setSelectedRecherche(res.data.recherche);
      setRechercheProspects(res.data.prospects || []);
      setSelectedSaved(new Set());
    } catch (e) {
      console.error('Erreur chargement recherche:', e);
    }
  };

  const handleDeleteRecherche = async (id) => {
    if (!window.confirm('Supprimer cette recherche et tous ses prospects ?')) return;
    try {
      await prospectionApi.deleteRecherche(id);
      setRecherches(r => r.filter(x => x.id !== id));
      if (selectedRecherche?.id === id) {
        setSelectedRecherche(null);
        setRechercheProspects([]);
      }
    } catch (e) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleUpdateStatut = async (prospectId, statut) => {
    try {
      await prospectionApi.updateProspect(prospectId, { statut });
      setRechercheProspects(prev => prev.map(p =>
        p.id === prospectId ? { ...p, statut } : p
      ));
    } catch (e) {
      alert('Erreur mise à jour');
    }
  };

  const handleDeleteProspects = async () => {
    if (selectedSaved.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedSaved.size} prospect(s) ?`)) return;
    try {
      const ids = Array.from(selectedSaved);
      await prospectionApi.deleteProspects(ids);
      setRechercheProspects(prev => prev.filter(p => !selectedSaved.has(p.id)));
      setSelectedSaved(new Set());
    } catch (e) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleExportCSV = async (rechId) => {
    try {
      const res = await prospectionApi.exportCSV(rechId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `prospection_${rechId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erreur export');
    }
  };

  const handleExportXLSX = async (rechId) => {
    try {
      const res = await prospectionApi.exportXLSX(rechId);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prospection_${rechId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erreur export Excel');
    }
  };

  const handleEnrich = async (prospectId) => {
    setEnrichingIds(prev => new Set(prev).add(prospectId));
    try {
      const res = await prospectionApi.enrich(prospectId);
      setRechercheProspects(prev => prev.map(p => p.id === prospectId ? res.data.prospect : p));
      setQuotaReached(!!res.data.quota?.reached);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur enrichissement');
    } finally {
      setEnrichingIds(prev => { const n = new Set(prev); n.delete(prospectId); return n; });
    }
  };

  const handleEnrichBulk = async () => {
    const ids = Array.from(selectedSaved);
    if (ids.length === 0) return;
    if (ids.length > 25) { alert('Maximum 25 prospects par batch'); return; }
    if (!window.confirm(`Enrichir ${ids.length} prospect(s) ? Le traitement séquentiel peut prendre jusqu'à ${ids.length * 30} secondes.`)) return;
    setEnrichingIds(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; });
    try {
      const res = await prospectionApi.enrichBulk(ids);
      const byId = new Map((res.data.results || []).filter(r => r.success).map(r => [r.id, r.prospect]));
      setRechercheProspects(prev => prev.map(p => byId.has(p.id) ? byId.get(p.id) : p));
      setQuotaReached(!!res.data.quota?.reached);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur enrichissement bulk');
    } finally {
      setEnrichingIds(prev => { const n = new Set(prev); ids.forEach(i => n.delete(i)); return n; });
    }
  };

  const handleShowAudit = async (prospectId) => {
    setShowAudit(prospectId);
    setAuditRows([]);
    try {
      const res = await prospectionApi.getEnrichissements(prospectId);
      setAuditRows(res.data.enrichissements || []);
    } catch (e) {
      console.error('Erreur audit', e);
    }
  };

  useEffect(() => {
    if (tab === 'sauvegardees') loadRecherches();
  }, [tab, loadRecherches]);

  useEffect(() => {
    if (tab !== 'sauvegardees') return;
    prospectionApi.getQuota().then(r => setQuotaReached(!!r.data?.reached)).catch(() => {});
  }, [tab]);

  // ── Score badge ──
  const ScoreBadge = ({ score }) => {
    const cls = score >= 50 ? 'high' : score >= 25 ? 'medium' : 'low';
    return <span className={`score-badge ${cls}`}>{score}</span>;
  };

  // ── Rendu ──
  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'recherche' ? 'active' : ''} onClick={() => { setTab('recherche'); setSelectedRecherche(null); }}>
          🔍 Recherche
        </button>
        <button className={tab === 'sauvegardees' ? 'active' : ''} onClick={() => setTab('sauvegardees')}>
          📋 Recherches sauvegardées
        </button>
      </div>

      {/* ════════════════════════════════════════════════════
          ONGLET RECHERCHE
          ════════════════════════════════════════════════════ */}
      {tab === 'recherche' && (
        <>
          <div className="prospection-search-form">
            <div className="prospection-field">
              <label>Recherche libre</label>
              <input
                type="text"
                placeholder="Nom, activité, dirigeant..."
                value={searchParams.q}
                onChange={e => setSearchParams(p => ({ ...p, q: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="prospection-field">
              <label>Code APE / NAF</label>
              <input
                type="text"
                placeholder="Ex: 45.11Z"
                value={searchParams.code_naf}
                onChange={e => setSearchParams(p => ({ ...p, code_naf: e.target.value }))}
              />
            </div>
            <div className="prospection-field">
              <label>Département</label>
              <select
                value={searchParams.departement}
                onChange={e => setSearchParams(p => ({ ...p, departement: e.target.value }))}
              >
                <option value="">Tous</option>
                {DEPARTEMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="prospection-field">
              <label>Code postal</label>
              <input
                type="text"
                placeholder="Ex: 75001"
                value={searchParams.code_postal}
                onChange={e => setSearchParams(p => ({ ...p, code_postal: e.target.value }))}
              />
            </div>
            <div className="prospection-field">
              <label>Forme juridique</label>
              <select
                value={searchParams.nature_juridique}
                onChange={e => setSearchParams(p => ({ ...p, nature_juridique: e.target.value }))}
              >
                <option value="">Toutes</option>
                {NATURES_JURIDIQUES.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
              </select>
            </div>
            <div className="prospection-field">
              <label>Statut</label>
              <select
                value={searchParams.etat_administratif}
                onChange={e => setSearchParams(p => ({ ...p, etat_administratif: e.target.value }))}
              >
                <option value="">Tous</option>
                <option value="A">Active</option>
                <option value="C">Cessée</option>
              </select>
            </div>
            <div className="prospection-field">
              <label>Tranche effectif</label>
              <select
                value={searchParams.tranche_effectif_salarie}
                onChange={e => setSearchParams(p => ({ ...p, tranche_effectif_salarie: e.target.value }))}
              >
                <option value="">Toutes</option>
                {TRANCHES_EFFECTIF.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div className="prospection-bool-filters">
              {[
                { key: 'est_ess', label: 'ESS' },
                { key: 'est_rge', label: 'RGE' },
                { key: 'est_bio', label: 'Bio' },
                { key: 'est_association', label: 'Association' },
                { key: 'est_entrepreneur_individuel', label: 'Entrepreneur individuel' },
              ].map(f => (
                <label key={f.key}>
                  <input
                    type="checkbox"
                    checked={searchParams[f.key]}
                    onChange={e => setSearchParams(p => ({ ...p, [f.key]: e.target.checked }))}
                  />
                  {f.label}
                </label>
              ))}
            </div>

            <div className="prospection-search-actions">
              <button className="prospection-btn-search" onClick={() => handleSearch()} disabled={searching}>
                {searching ? '⏳ Recherche...' : '🔍 Rechercher'}
              </button>
              <button className="prospection-btn-reset" onClick={handleReset}>Réinitialiser</button>
              {hasSearched && (
                <span className="prospection-results-count">
                  <strong>{totalResults.toLocaleString('fr-FR')}</strong> résultat(s)
                </span>
              )}
            </div>
          </div>

          {/* Barre sélection */}
          {results.length > 0 && selected.size > 0 && (
            <div className="prospection-bulk-bar">
              <strong>{selected.size}</strong> sélectionné(s) sur {results.length}
              <button className="prospection-btn-reset" onClick={() => setSelected(new Set())}>Désélectionner</button>
            </div>
          )}

          {/* Tableau résultats */}
          {results.length > 0 && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={selected.size === results.length && results.length > 0}
                        onChange={e => {
                          if (e.target.checked) setSelected(new Set(results.map((_, i) => i)));
                          else setSelected(new Set());
                        }}
                      />
                    </th>
                    <th>Entreprise</th>
                    <th>Activité</th>
                    <th>Localisation</th>
                    <th>Dirigeant</th>
                    <th>Création</th>
                    <th>Effectif</th>
                    <th>Score</th>
                    <th style={{ width: 60 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.siren + '-' + i}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(i)}
                          onChange={e => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(i); else next.delete(i);
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{r.nom_complet}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.siren}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>{r.activite_principale}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.libelle_activite}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>{r.commune}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.code_postal} ({r.departement})</div>
                      </td>
                      <td>
                        {r.dirigeant_principal ? (
                          <>
                            <div style={{ fontSize: 13 }}>{r.dirigeant_principal.prenoms} {r.dirigeant_principal.nom}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.dirigeant_principal.qualite}</div>
                          </>
                        ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {r.date_creation ? new Date(r.date_creation).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>{r.categorie_entreprise || '—'}</td>
                      <td><ScoreBadge score={r.score} /></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" title="Détail" onClick={() => setShowDetail(r)}>👁️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasSearched && results.length === 0 && !searching && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Aucun résultat trouvé. Essayez d'élargir vos critères de recherche.
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="prospection-pagination">
              <button disabled={currentPage <= 1} onClick={() => handleSearch(currentPage - 1)}>
                ‹ Préc.
              </button>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                const start = Math.max(1, Math.min(currentPage - 4, totalPages - 9));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} className={p === currentPage ? 'active' : ''} onClick={() => handleSearch(p)}>
                    {p}
                  </button>
                );
              })}
              <button disabled={currentPage >= totalPages} onClick={() => handleSearch(currentPage + 1)}>
                Suiv. ›
              </button>
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════
          ONGLET RECHERCHES SAUVEGARDÉES
          ════════════════════════════════════════════════════ */}
      {tab === 'sauvegardees' && !selectedRecherche && (
        <>
          {loadingRecherches ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
          ) : recherches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Aucune recherche sauvegardée. Lancez une recherche puis cliquez sur "Sauvegarder".
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Critères</th>
                    <th>Prospects</th>
                    <th>Date</th>
                    <th>Créé par</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recherches.map(r => (
                    <tr key={r.id} className="prospection-recherche-card" onClick={() => loadRecherche(r.id)}>
                      <td style={{ fontWeight: 600 }}>{r.nom}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {Object.entries(r.criteres || {})
                          .filter(([, v]) => v && v !== '' && v !== false)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(' | ') || '—'}
                      </td>
                      <td><strong>{r.nb_prospects || r.total_resultats || 0}</strong></td>
                      <td style={{ fontSize: 13 }}>
                        {new Date(r.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {r.creator_prenom ? `${r.creator_prenom} ${r.creator_nom}` : r.creator_nom || '—'}
                      </td>
                      <td>
                        <div className="action-buttons" onClick={e => e.stopPropagation()}>
                          <button className="btn-icon" title="Voir" onClick={() => loadRecherche(r.id)}>👁️</button>
                          {editMode && user.role === 'super-admin' && (
                            <button className="btn-icon delete" title="Supprimer" onClick={() => handleDeleteRecherche(r.id)}>🗑️</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Détail recherche sauvegardée ── */}
      {tab === 'sauvegardees' && selectedRecherche && (
        <>
          <div className="page-toolbar">
            <button className="prospection-btn-reset" onClick={() => { setSelectedRecherche(null); setRechercheProspects([]); }}>
              ← Retour
            </button>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{selectedRecherche.nom}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {rechercheProspects.length} prospect(s)
            </span>
          </div>

          {selectedSaved.size > 0 && (
            <div className="prospection-bulk-bar">
              <strong>{selectedSaved.size}</strong> sélectionné(s)
              <button
                className="prospection-btn-enrich"
                disabled={quotaReached}
                onClick={handleEnrichBulk}
                title={quotaReached ? 'Quota Google CSE atteint' : 'Enrichir la sélection (max 25)'}
              >
                ⚡ Enrichir la sélection
              </button>
              {editMode && user.role === 'super-admin' && (
                <button className="prospection-btn-reset" style={{ color: 'var(--color-error)' }} onClick={handleDeleteProspects}>
                  🗑️ Supprimer la sélection
                </button>
              )}
            </div>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selectedSaved.size === rechercheProspects.length && rechercheProspects.length > 0}
                      onChange={e => {
                        if (e.target.checked) setSelectedSaved(new Set(rechercheProspects.map(p => p.id)));
                        else setSelectedSaved(new Set());
                      }}
                    />
                  </th>
                  <th>Entreprise</th>
                  <th>Localisation</th>
                  <th>Dirigeant</th>
                  <th style={{ minWidth: 240 }}>Contacts</th>
                  <th>Score</th>
                  <th>Statut</th>
                  <th style={{ width: 60 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rechercheProspects.map(p => (
                  <tr key={p.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSaved.has(p.id)}
                        onChange={e => {
                          const next = new Set(selectedSaved);
                          if (e.target.checked) next.add(p.id); else next.delete(p.id);
                          setSelectedSaved(next);
                        }}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nom_complet}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.siren}</div>
                      {p.libelle_activite && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.libelle_activite}</div>}
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{p.commune}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.code_postal}</div>
                    </td>
                    <td>
                      {p.dirigeant_prenom || p.dirigeant_nom ? (
                        <>
                          <div style={{ fontSize: 13 }}>{p.dirigeant_prenom} {p.dirigeant_nom}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.dirigeant_qualite}</div>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      <ProspectionContactsCell
                        prospect={p}
                        isEnriching={enrichingIds.has(p.id)}
                        onEnrich={() => handleEnrich(p.id)}
                        onShowAudit={user.role === 'super-admin' ? () => handleShowAudit(p.id) : null}
                        quotaReached={quotaReached}
                      />
                    </td>
                    <td><ScoreBadge score={p.score} /></td>
                    <td>
                      <select
                        className="page-select"
                        value={p.statut || 'nouveau'}
                        onChange={e => handleUpdateStatut(p.id, e.target.value)}
                        style={{ fontSize: 12, height: 28 }}
                      >
                        {Object.entries(STATUT_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Détail" onClick={() => setShowDetail(p)}>👁️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════
          MODALE DÉTAIL ENTREPRISE
          ════════════════════════════════════════════════════ */}
      {showDetail && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showDetail.nom_complet}</h2>
              <button className="modal-close" onClick={() => setShowDetail(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="prospection-detail-grid">
                {/* Infos entreprise */}
                <div className="prospection-detail-section">
                  <h4>🏢 Entreprise</h4>
                  <div className="prospection-detail-row"><span>SIREN</span><span>{showDetail.siren}</span></div>
                  <div className="prospection-detail-row"><span>SIRET (siège)</span><span>{showDetail.siret || '—'}</span></div>
                  <div className="prospection-detail-row"><span>Activité</span><span>{showDetail.activite_principale} — {showDetail.libelle_activite}</span></div>
                  <div className="prospection-detail-row"><span>Forme juridique</span><span>{showDetail.libelle_nature_juridique || showDetail.nature_juridique}</span></div>
                  <div className="prospection-detail-row"><span>Date création</span><span>{showDetail.date_creation ? new Date(showDetail.date_creation).toLocaleDateString('fr-FR') : '—'}</span></div>
                  <div className="prospection-detail-row"><span>Catégorie</span><span>{showDetail.categorie_entreprise || '—'}</span></div>
                  <div className="prospection-detail-row"><span>Établissements</span><span>{showDetail.nombre_etablissements || '—'}</span></div>
                </div>

                {/* Adresse */}
                <div className="prospection-detail-section">
                  <h4>📍 Adresse siège</h4>
                  <div className="prospection-detail-row"><span>Adresse</span><span>{showDetail.adresse || '—'}</span></div>
                  <div className="prospection-detail-row"><span>Ville</span><span>{showDetail.commune}</span></div>
                  <div className="prospection-detail-row"><span>Code postal</span><span>{showDetail.code_postal}</span></div>
                  <div className="prospection-detail-row"><span>Département</span><span>{showDetail.departement}</span></div>
                </div>

                {/* Dirigeants */}
                <div className="prospection-detail-section">
                  <h4>👤 Dirigeant(s)</h4>
                  {(showDetail.dirigeants || []).length > 0 ? (
                    showDetail.dirigeants.map((d, i) => (
                      <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: i < showDetail.dirigeants.length - 1 ? '1px solid var(--border-default)' : 'none' }}>
                        <div className="prospection-detail-row"><span>Nom</span><span>{d.prenoms} {d.nom}</span></div>
                        <div className="prospection-detail-row"><span>Fonction</span><span>{d.qualite}</span></div>
                      </div>
                    ))
                  ) : showDetail.dirigeant_nom ? (
                    <>
                      <div className="prospection-detail-row"><span>Nom</span><span>{showDetail.dirigeant_prenom} {showDetail.dirigeant_nom}</span></div>
                      <div className="prospection-detail-row"><span>Fonction</span><span>{showDetail.dirigeant_qualite}</span></div>
                    </>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucun dirigeant identifié</div>
                  )}
                </div>

                {/* Score + Email */}
                <div className="prospection-detail-section">
                  <h4>📊 Score : <ScoreBadge score={showDetail.score} /></h4>
                  <div className="score-detail-list">
                    {Object.entries(showDetail.score_details || {}).map(([k, v]) => (
                      <div key={k} className="score-detail-item">
                        <span>{SCORE_DETAIL_LABELS[k] || k}</span>
                        <span>+{v} pts</span>
                      </div>
                    ))}
                    {Object.keys(showDetail.score_details || {}).length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Aucun signal détecté</div>
                    )}
                  </div>

                  {showDetail.email_estime && (
                    <>
                      <h4 style={{ marginTop: 14 }}>📧 Patterns email estimés</h4>
                      <div className="prospection-email-patterns">
                        {(showDetail.email_estime.patterns || []).map((p, i) => (
                          <span key={i} className="prospection-email-pattern">{p}</span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          MODALE AUDIT ENRICHISSEMENT (super-admin)
          ════════════════════════════════════════════════════ */}
      {showAudit && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔌 Sources d'enrichissement</h2>
              <button className="modal-close" onClick={() => setShowAudit(null)}>✕</button>
            </div>
            <div className="modal-body">
              {auditRows.length === 0 ? (
                <div style={{ color: 'var(--text-secondary)' }}>Aucun enrichissement enregistré.</div>
              ) : (
                <table style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Date</th>
                      <th style={{ textAlign: 'left' }}>Canal</th>
                      <th style={{ textAlign: 'left' }}>Source</th>
                      <th style={{ textAlign: 'left' }}>Valeur</th>
                      <th style={{ textAlign: 'left' }}>Succès</th>
                      <th style={{ textAlign: 'left' }}>Erreur / Métadonnées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.map(r => (
                      <tr key={r.id}>
                        <td>{new Date(r.created_at).toLocaleString('fr-FR')}</td>
                        <td>{r.canal}</td>
                        <td>{r.source}</td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.valeur || '—'}</td>
                        <td>{r.success ? '✓' : '✗'}</td>
                        <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                          {r.error || (r.metadata ? JSON.stringify(r.metadata).slice(0, 100) : '')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          MODALE SAUVEGARDER
          ════════════════════════════════════════════════════ */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💾 Sauvegarder la recherche</h2>
              <button className="modal-close" onClick={() => setShowSaveModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="prospection-field" style={{ marginBottom: 16 }}>
                <label>Nom de la recherche</label>
                <input
                  type="text"
                  placeholder="Ex: Garages auto 75 - Avril 2026"
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  autoFocus
                />
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {selected.size > 0
                  ? `${selected.size} prospect(s) sélectionné(s) seront sauvegardés.`
                  : `Les ${results.length} résultats de la page seront sauvegardés.`
                }
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSaveModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || !saveName.trim()}>
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProspectionB2B;
